import {Color} from '@benchling/nucleus-ui';
import React from 'react';

import {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import staticURL from 'lib/staticURL';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

/** Default when no custom image; same glyph as Model Hub in the nav. */
const DEFAULT_MODEL_EXTENSION_ICON_NAME: BenchlingIconName = 'hexagon-nodes';
/** Default for MCP server extensions without a directory image; matches MCP tool default in chat. */
const DEFAULT_MCP_EXTENSION_ICON_NAME: BenchlingIconName = 'plug';
/** Default for agent skill extensions; matches the agent skill icon used elsewhere. */
const DEFAULT_AGENT_SKILL_EXTENSION_ICON_NAME: BenchlingIconName = 'wand-magic-sparkles';
const DEFAULT_BENCHLING_APP_EXTENSION_ICON_NAME: BenchlingIconName = 'puzzle-piece';
const DEFAULT_EXTENSION_ICON_COLOR = Color.BLUE_400;

/** Resolve icon URLs that may be absolute (CloudFront), protocol-relative (CDN), or /static/... */
export function resolveExtensionIconSrc(iconUrl: string): string {
  if (/^https?:\/\//.test(iconUrl) || iconUrl.startsWith('//')) {
    return iconUrl;
  }
  return staticURL(iconUrl);
}

export interface GetExtensionIconOptions {
  className?: string;
  /** Resolved CloudFront URL from extension definition assets. */
  iconUrl?: string | null;
  /** Chooses the fallback Benchling icon when iconUrl is unavailable. */
  extensionType: ExtensionType;
}

/** Fallback Benchling icon when there is no catalog iconUrl. */
function defaultBenchlingIconForExtensionType(extensionType: ExtensionType): BenchlingIconName {
  switch (extensionType) {
    case ExtensionType.AGENT_SKILL:
      return DEFAULT_AGENT_SKILL_EXTENSION_ICON_NAME;
    case ExtensionType.BENCHLING_APP:
      return DEFAULT_BENCHLING_APP_EXTENSION_ICON_NAME;
    case ExtensionType.MCP_SERVER:
      return DEFAULT_MCP_EXTENSION_ICON_NAME;
    case ExtensionType.MODEL:
      return DEFAULT_MODEL_EXTENSION_ICON_NAME;
    default:
      return assertNever(extensionType);
  }
}

export function getExtensionIcon(_apiId: string, options: GetExtensionIconOptions): React.ReactElement {
  if (options.iconUrl) {
    return <img src={resolveExtensionIconSrc(options.iconUrl)} alt="" className={options.className} />;
  }

  const iconName = defaultBenchlingIconForExtensionType(options.extensionType);

  return (
    <span className={options.className} style={{color: DEFAULT_EXTENSION_ICON_COLOR}}>
      <BenchlingIcon icon={iconName} />
    </span>
  );
}
